import { useEffect, useState } from 'react';

/** De quanto em quanto tempo a aba aberta confere se saiu versão nova. */
const CHECK_EVERY = 5 * 60_000;
/** O script de entrada que o build põe no index.html: `/assets/index-<hash>.js`. */
const ENTRY = /\/assets\/index-[\w-]+\.js/;

/** O script de entrada desta aba; no `vite dev` não há, e a conferência fica desligada. */
const loadedEntry = (): string | null => {
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const entry = script.getAttribute('src')?.match(ENTRY)?.[0];
    if (entry) return entry;
  }
  return null;
};

/**
 * A aba aberta antes de um deploy segue com a tela antiga, falando com a API
 * nova — e mostra o que mudou do jeito antigo. Ao voltar para a aba e a cada
 * 5 minutos, isto confere o index.html publicado: se o script de entrada é
 * outro, saiu versão nova.
 */
export function useNewVersion(): boolean {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const current = loadedEntry();
    if (!current || stale) return undefined;
    let cancelled = false;
    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const html = await (await fetch('/', { cache: 'no-store' })).text();
        const published = html.match(ENTRY)?.[0];
        if (!cancelled && published && published !== current) setStale(true);
      } catch {
        // Sem rede agora: confere de novo na próxima vez.
      }
    };
    const timer = window.setInterval(() => void check(), CHECK_EVERY);
    const onVisible = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [stale]);

  return stale;
}
